// src/services/pharmacy_api.js
import axios from 'axios';

const API_BASE_URL = 'https://ourzestapp.online/oz_backend/pharmacy_module';
let authToken = null;
let currentUser = null;
let currentShop = null;

export const setAuthData = (user, shop) => {
  currentUser = user;
  currentShop = shop;
  if (user && user.token) authToken = user.token;
};

const getHeaders = () => ({
  'Content-Type': 'application/json',
  ...(authToken && { 'Authorization': `Bearer ${authToken}` })
});

const apiRequest = async (endpoint, data) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/${endpoint}`, data, { headers: getHeaders() });
    return response.data;
  } catch (error) {
    console.error(`API Error (${endpoint}):`, error);
    return { status: 'error', message: error.message, data: [] };
  }
};

// Helper to safely get number from API response
const safeNumber = (value, defaultValue = 0) => {
  if (value === null || value === undefined || value === '') return defaultValue;
  const num = parseFloat(value);
  return isNaN(num) ? defaultValue : num;
};

// Helper to safely get array from API response
const safeArray = (response) => {
  if (!response) return [];
  if (Array.isArray(response)) return response;
  if (response.data && Array.isArray(response.data)) return response.data;
  if (response.data && response.data.data && Array.isArray(response.data.data)) return response.data.data;
  if (Array.isArray(response.data)) return response.data;
  return [];
};

// ==================== FETCH PRODUCT STATS ====================
export const fetchProductStats = async (shopId) => {
  if (!shopId) {
    return {
      expired_count: 0,
      out_of_stock_count: 0,
      disposed_count: 0,
      discount_count: 0,
      bonus_count: 0,
      expiring_count: 0,
      expiring_products: [],
      expired_products: [],
      out_of_stock_products: [],
      disposed_products: [],
      discount_products: [],
      bonus_products: [],
    };
  }

  try {
    // Fetch all products
    const productsResponse = await apiRequest('pharmacy_products.php', {
      action: 'get_records',
      shop_id: shopId,
      limit: 10000,
    });

    const products = safeArray(productsResponse);
    const today = new Date();
    const thirtyDaysFromNow = new Date(today);
    thirtyDaysFromNow.setDate(today.getDate() + 30);

    let expiredCount = 0;
    let outOfStockCount = 0;
    let disposedCount = 0;
    let discountCount = 0;
    let bonusCount = 0;
    let expiringCount = 0;

    const expiredProducts = [];
    const outOfStockProducts = [];
    const disposedProducts = [];
    const discountProducts = [];
    const bonusProducts = [];
    const expiringProducts = [];

    products.forEach(product => {
      const quantity = safeNumber(product.total_quantity || product.quantity || 0);
      const expiryDate = product.expiry_date ? new Date(product.expiry_date) : null;
      
      const bonusBuy = safeNumber(product.bonus_buy);
      const bonusFree = safeNumber(product.bonus_free);
      const hasBonus = (bonusBuy > 0 || bonusFree > 0);
      
      const tradePercent = safeNumber(product.trade_percent);
      const extraPercent = safeNumber(product.extra_percent);
      const effectiveDiscount = safeNumber(product.effective_discount_percent);
      const hasDiscount = (tradePercent > 0 || extraPercent > 0 || effectiveDiscount > 0);

      if (expiryDate && expiryDate < today) {
        expiredCount++;
        expiredProducts.push({
          id: product.id,
          product_name: product.product_name || 'Unknown',
          batch_no: product.batch_no || '-',
          expiry_date: product.expiry_date,
          quantity: quantity,
          unit: product.unit_of_measure || 'pcs',
        });
      }

      if (expiryDate && expiryDate >= today && expiryDate <= thirtyDaysFromNow) {
        expiringCount++;
        expiringProducts.push({
          id: product.id,
          product_name: product.product_name || 'Unknown',
          batch_no: product.batch_no || '-',
          expiry_date: product.expiry_date,
          quantity: quantity,
          unit: product.unit_of_measure || 'pcs',
        });
      }

      if (quantity <= 0) {
        outOfStockCount++;
        outOfStockProducts.push({
          id: product.id,
          product_name: product.product_name || 'Unknown',
          batch_no: product.batch_no || '-',
          quantity: quantity,
          unit: product.unit_of_measure || 'pcs',
        });
      }

      if (hasDiscount) {
        discountCount++;
        discountProducts.push({
          id: product.id,
          product_name: product.product_name || 'Unknown',
          batch_no: product.batch_no || '-',
          trade_percent: tradePercent,
          extra_percent: extraPercent,
          effective_discount_percent: effectiveDiscount,
          quantity: quantity,
          unit: product.unit_of_measure || 'pcs',
        });
      }

      if (hasBonus) {
        bonusCount++;
        bonusProducts.push({
          id: product.id,
          product_name: product.product_name || 'Unknown',
          batch_no: product.batch_no || '-',
          bonus_buy: bonusBuy,
          bonus_free: bonusFree,
          quantity: quantity,
          unit: product.unit_of_measure || 'pcs',
        });
      }
    });

    // For disposed products, use expired products with zero quantity
    const expiredZeroQuantity = products.filter(p => {
      const qty = safeNumber(p.total_quantity || p.quantity || 0);
      const exp = p.expiry_date ? new Date(p.expiry_date) : null;
      return qty <= 0 && exp && exp < today;
    });
    disposedCount = expiredZeroQuantity.length;
    expiredZeroQuantity.forEach(p => {
      disposedProducts.push({
        id: p.id,
        product_name: p.product_name || 'Unknown',
        batch_no: p.batch_no || '-',
        quantity: safeNumber(p.total_quantity || p.quantity || 0),
        unit: p.unit_of_measure || 'pcs',
        disposed_date: p.expiry_date || '',
        reason: 'Expired',
      });
    });

    return {
      expired_count: expiredCount,
      out_of_stock_count: outOfStockCount,
      disposed_count: disposedCount,
      discount_count: discountCount,
      bonus_count: bonusCount,
      expiring_count: expiringCount,
      expiring_products: expiringProducts,
      expired_products: expiredProducts,
      out_of_stock_products: outOfStockProducts,
      disposed_products: disposedProducts,
      discount_products: discountProducts,
      bonus_products: bonusProducts,
    };
  } catch (error) {
    console.error('Error fetching product stats:', error);
    return {
      expired_count: 0,
      out_of_stock_count: 0,
      disposed_count: 0,
      discount_count: 0,
      bonus_count: 0,
      expiring_count: 0,
      expiring_products: [],
      expired_products: [],
      out_of_stock_products: [],
      disposed_products: [],
      discount_products: [],
      bonus_products: [],
    };
  }
};

// ==================== FETCH DASHBOARD DATA ====================
export const fetchDashboardData = async (filters = {}) => {
  if (!currentUser) throw new Error('User not authenticated');
  const shopId = currentShop?.shop_id || currentUser?.shop_id;
  if (!shopId) return { stats: {}, trend: { labels: [], values: [] }, recent_sales: [], low_stock: [] };

  const dashboardData = { stats: {}, trend: { labels: [], values: [] }, recent_sales: [], low_stock: [] };

  try {
    // Build date filters for API calls
    const dateFilters = {};
    if (filters.startDate) {
      dateFilters.start_date = filters.startDate;
    }
    if (filters.endDate) {
      dateFilters.end_date = filters.endDate;
    }

    // Fetch data in parallel with proper date filters
    const [customersRes, suppliersRes, productsRes, allSalesRes, salesRes, purchasesRes, transactionsRes, customerBalancesRes] = await Promise.all([
      apiRequest('pharmacy_customers.php', { action: 'get_records', shop_id: shopId, username: currentUser?.username }),
      apiRequest('pharmacy_suppliers.php', { action: 'list', shop_id: shopId }),
      apiRequest('pharmacy_products.php', { action: 'get_records', shop_id: shopId, username: currentUser?.username }),
      apiRequest('pharmacy_sell.php', { action: 'get_records', shop_id: shopId }), // All sales - no filter
      apiRequest('pharmacy_sell.php', { 
        action: 'get_records', 
        shop_id: shopId,
        ...dateFilters // Pass date filters here
      }),
      apiRequest('pharmacy_purchases.php', { 
        action: 'get_records', 
        shop_id: shopId,
        username: currentUser?.username,
        ...dateFilters // Pass date filters here
      }),
      apiRequest('pharmacy_transactions.php', {
        action: 'get_transactions',
        shop_id: shopId,
        limit: 10000,
        ...dateFilters
      }),
      apiRequest('mechanics_customer.php', {
        action: 'list',
        shop_id: shopId,
      })
    ]);

    const productList = safeArray(productsRes);
    const allSalesList = safeArray(allSalesRes);
    const salesList = safeArray(salesRes);
    const purchasesList = safeArray(purchasesRes);
    const transactions = safeArray(transactionsRes);
    const customerData = safeArray(customerBalancesRes);
    
    const totalCustomers = safeNumber(customersRes?.total || customersRes?.data?.length || 0);
    const totalSuppliers = safeNumber(suppliersRes?.suppliers?.length || suppliersRes?.data?.length || 0);
    
    const overallSalesTotal = allSalesList.reduce((sum, sale) => sum + safeNumber(sale.grand_total), 0);
    const overallSalesCount = allSalesList.length;
    
    const monthSalesTotal = salesList.reduce((sum, sale) => sum + safeNumber(sale.grand_total), 0);
    const monthPurchasesTotal = purchasesList.reduce((sum, p) => sum + safeNumber(p.grand_total), 0);
    
    let inventoryValue = 0;
    let totalProducts = productList.length;
    
    productList.forEach(p => {
      const quantity = safeNumber(p.quantity || p.total_quantity || 0);
      if (quantity <= 0) return;
      
      const costPerPack = safeNumber(p.cost_per_pack || p.sell_per_pack || 0);
      const costPerUnit = safeNumber(p.cost_price || p.effective_cost_price || 0);
      const itemsPerPack = safeNumber(p.items_per_pack, 1);
      
      let value = 0;
      if (costPerPack > 0 && itemsPerPack > 1) {
        const fullPacks = Math.floor(quantity);
        const partialItems = quantity - fullPacks;
        const partialValue = partialItems * (costPerPack / itemsPerPack);
        value = (fullPacks * costPerPack) + partialValue;
      } else if (costPerUnit > 0) {
        value = costPerUnit * quantity;
      } else if (costPerPack > 0) {
        value = costPerPack * quantity;
      }
      inventoryValue += value;
    });
    
    const lowStockCount = productList.filter(p => safeNumber(p.quantity || p.total_quantity || 0) <= safeNumber(p.quantity_alert, 5)).length;
    
    // ====== CALCULATE CUSTOMER PAID AND BALANCES ======
    let customerPaid = 0;
    let customersBalances = 0;

    // Calculate total paid from customer transactions (completed transactions)
    transactions
      .filter(t => t.reference_type === 'customer' && t.status === 'completed')
      .forEach(t => {
        customerPaid += safeNumber(t.amount_paid);
      });

    // Calculate total balances (due amounts) from customer transactions
    transactions
      .filter(t => t.reference_type === 'customer' && safeNumber(t.amount_due) > 0)
      .forEach(t => {
        customersBalances += safeNumber(t.amount_due);
      });

    // Also add due amounts from mechanics_customer table
    if (Array.isArray(customerData)) {
      customerData.forEach(c => {
        const due = safeNumber(c.total_due || c.balance_due || 0);
        if (due > 0) {
          customersBalances += due;
        }
      });
    }

    // Also check po_customers_account table for additional balances
    try {
      const poCustomerRes = await apiRequest('pharmacy_customers.php', {
        action: 'get_balances',
        shop_id: shopId,
      });
      const poCustomers = safeArray(poCustomerRes);
      if (Array.isArray(poCustomers)) {
        poCustomers.forEach(c => {
          const due = safeNumber(c.balance_due || 0);
          if (due > 0) {
            customersBalances += due;
          }
        });
      }
    } catch (e) {
      console.log('Could not fetch PO customer balances:', e);
    }

    // Calculate customer paid from sales that have paid_amount > 0
    // This is another way to verify the paid amount
    let salesPaidTotal = 0;
    allSalesList.forEach(sale => {
      const paid = safeNumber(sale.paid_amount);
      if (paid > 0) {
        salesPaidTotal += paid;
      }
    });
    
    // Use the larger of the two paid amounts for better accuracy
    customerPaid = Math.max(customerPaid, salesPaidTotal);
    
    dashboardData.stats = {
      total_customers: totalCustomers,
      total_suppliers: totalSuppliers,
      total_products: totalProducts,
      overall_sales: overallSalesTotal,
      overall_sales_count: overallSalesCount,
      month_sales: monthSalesTotal,
      month_sales_count: salesList.length,
      total_purchases: monthPurchasesTotal,
      inventory_value: inventoryValue,
      low_stock_count: lowStockCount,
      customer_paid: customerPaid,
      customers_balances: customersBalances,
    };

    // Sales trend based on filtered data
    const salesByDate = {};
    salesList.forEach(sale => {
      const date = sale.date;
      if (date) {
        salesByDate[date] = (salesByDate[date] || 0) + safeNumber(sale.grand_total);
      }
    });
    const dates = Object.keys(salesByDate).sort();
    dashboardData.trend = { 
      labels: dates.map(d => {
        const date = new Date(d);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      }), 
      values: dates.map(d => salesByDate[d]) 
    };
    dashboardData.recent_sales = salesList.slice(0, 10);
    dashboardData.low_stock = productList
      .filter(p => safeNumber(p.quantity || p.total_quantity || 0) <= safeNumber(p.quantity_alert, 5))
      .slice(0, 20)
      .map(p => ({ 
        id: p.id, 
        product_name: p.product_name || 'Unknown', 
        batch_no: p.batch_no || '-', 
        quantity: p.quantity || p.total_quantity || 0, 
        unit: p.unit || p.unit_of_measure || 'pcs', 
        quantity_alert: p.quantity_alert || 5
      }));

    return dashboardData;
  } catch (error) {
    console.error('Error fetching dashboard data:', error);
    return dashboardData;
  }
};

// ==================== FETCH SERVICES DATA ====================
export const fetchServicesData = async (shopId, username, filters = {}) => {
  try {
    const requestData = {
      action: 'get_records',
      shop_id: shopId,
      username: username,
    };
    
    if (filters.startDate) {
      requestData.start_date = filters.startDate;
    }
    if (filters.endDate) {
      requestData.end_date = filters.endDate;
    }
    
    const response = await apiRequest('mechanics_service.php', requestData);
    const services = safeArray(response);
    return {
      total: services.length,
      pending: services.filter(s => s.status === 'pending').length,
      in_progress: services.filter(s => s.status === 'in_progress').length,
      completed: services.filter(s => s.status === 'completed').length,
      total_revenue: services.reduce((sum, s) => sum + safeNumber(s.grand_total), 0),
      recent: services.slice(0, 10),
    };
  } catch (error) {
    console.error('Error fetching services:', error);
    return { total: 0, pending: 0, in_progress: 0, completed: 0, total_revenue: 0, recent: [] };
  }
};

// ==================== FETCH MOBCHANGE DATA ====================
export const fetchMobchangeData = async (shopId, username, filters = {}) => {
  try {
    const requestData = {
      action: 'get_records',
      shop_id: shopId,
      username: username,
    };
    
    if (filters.startDate) {
      requestData.start_date = filters.startDate;
    }
    if (filters.endDate) {
      requestData.end_date = filters.endDate;
    }
    
    const response = await apiRequest('mechanics_mobchange.php', requestData);
    const mobchanges = safeArray(response);
    return {
      total: mobchanges.length,
      pending: mobchanges.filter(m => m.status === 'pending').length,
      completed: mobchanges.filter(m => m.status === 'completed' || m.status === 'delivered').length,
      total_revenue: mobchanges.reduce((sum, m) => sum + safeNumber(m.grand_total), 0),
      recent: mobchanges.slice(0, 10),
    };
  } catch (error) {
    console.error('Error fetching mobchanges:', error);
    return { total: 0, pending: 0, completed: 0, total_revenue: 0, recent: [] };
  }
};

// ==================== FETCH RETURNS DATA ====================
export const fetchReturnsData = async (shopId, username, filters = {}) => {
  try {
    const requestData = {
      action: 'get_records',
      shop_id: shopId,
      username: username,
    };
    
    if (filters.startDate) {
      requestData.start_date = filters.startDate;
    }
    if (filters.endDate) {
      requestData.end_date = filters.endDate;
    }
    
    const response = await apiRequest('pharmacy_return.php', requestData);
    
    let returns = [];
    let totalCount = 0;
    
    if (response && response.status === 'success') {
      if (response.data && Array.isArray(response.data)) {
        returns = response.data;
        totalCount = response.total || returns.length;
      } else if (Array.isArray(response)) {
        returns = response;
        totalCount = returns.length;
      }
    } else if (Array.isArray(response)) {
      returns = response;
      totalCount = returns.length;
    } else if (response && response.data && Array.isArray(response.data)) {
      returns = response.data;
      totalCount = response.total || returns.length;
    }
    
    const totalAmount = returns.reduce((sum, r) => {
      const amount = safeNumber(r.refund_amount) || safeNumber(r.grand_total) || 0;
      return sum + amount;
    }, 0);
    
    return {
      total: totalCount,
      total_amount: totalAmount,
      recent: returns.slice(0, 10),
    };
  } catch (error) {
    console.error('Error fetching returns:', error);
    return { total: 0, total_amount: 0, recent: [] };
  }
};

// ==================== FETCH EMPLOYEES DATA ====================
export const fetchEmployeesData = async (shopId, username) => {
  try {
    const response = await apiRequest('pharmacy_employees.php', {
      action: 'get_records',
      shop_id: shopId,
      username: username,
    });
    const employees = safeArray(response);
    
    let payments = [];
    try {
      const paymentsRes = await apiRequest('pharmacy_employees.php', {
        action: 'get_payments',
        shop_id: shopId,
        limit: 100,
      });
      payments = safeArray(paymentsRes);
    } catch (e) {
      console.error('Error fetching payments:', e);
    }
    
    return {
      total: employees.length,
      active: employees.filter(e => e.status === 'active').length,
      total_salary_paid: payments.reduce((sum, p) => sum + safeNumber(p.amount), 0),
      recent: employees.slice(0, 10),
    };
  } catch (error) {
    console.error('Error fetching employees:', error);
    return { total: 0, active: 0, total_salary_paid: 0, recent: [] };
  }
};

// ==================== FETCH TRANSACTIONS DATA ====================
export const fetchTransactionsData = async (shopId, filters = {}) => {
  try {
    const requestData = {
      action: 'get_transactions',
      shop_id: shopId,
      limit: 100,
    };
    
    if (filters.startDate) {
      requestData.start_date = filters.startDate;
    }
    if (filters.endDate) {
      requestData.end_date = filters.endDate;
    }
    
    const response = await apiRequest('pharmacy_transactions.php', requestData);
    const transactions = safeArray(response);
    return {
      customer: transactions.filter(t => t.reference_type === 'customer'),
      supplier: transactions.filter(t => t.reference_type === 'supplier'),
      employee: transactions.filter(t => t.reference_type === 'employee'),
      expense: transactions.filter(t => t.reference_type === 'expense'),
    };
  } catch (error) {
    console.error('Error fetching transactions:', error);
    return { customer: [], supplier: [], employee: [], expense: [] };
  }
};

// ==================== FETCH PURCHASES DATA ====================
export const fetchPurchasesData = async (shopId, username, filters = {}) => {
  try {
    const requestData = {
      action: 'get_records',
      shop_id: shopId,
      username: username,
    };
    
    if (filters.startDate) {
      requestData.start_date = filters.startDate;
    }
    if (filters.endDate) {
      requestData.end_date = filters.endDate;
    }
    
    const response = await apiRequest('pharmacy_purchases.php', requestData);
    const purchases = safeArray(response);
    return {
      total: purchases.length,
      total_amount: purchases.reduce((sum, p) => sum + safeNumber(p.grand_total), 0),
      recent: purchases.slice(0, 10),
    };
  } catch (error) {
    console.error('Error fetching purchases:', error);
    return { total: 0, total_amount: 0, recent: [] };
  }
};

// ==================== FETCH CUSTOMER BALANCES ====================
export const fetchCustomerBalances = async (shopId) => {
  try {
    const response = await apiRequest('pharmacy_customers.php', {
      action: 'get_balances',
      shop_id: shopId,
    });
    return safeArray(response);
  } catch (error) {
    console.error('Error fetching customer balances:', error);
    return [];
  }
};

// ==================== FETCH ALL CUSTOMER TRANSACTIONS ====================
export const fetchAllCustomerTransactions = async (shopId, filters = {}) => {
  try {
    const requestData = {
      action: 'get_transactions',
      shop_id: shopId,
      limit: 10000,
      reference_type: 'customer',
    };
    
    if (filters.startDate) {
      requestData.start_date = filters.startDate;
    }
    if (filters.endDate) {
      requestData.end_date = filters.endDate;
    }
    
    const response = await apiRequest('pharmacy_transactions.php', requestData);
    return safeArray(response);
  } catch (error) {
    console.error('Error fetching customer transactions:', error);
    return [];
  }
};

// ==================== FETCH CUSTOMER PAYMENT SUMMARY ====================
export const fetchCustomerPaymentSummary = async (shopId) => {
  try {
    const [transactions, customers] = await Promise.all([
      fetchAllCustomerTransactions(shopId),
      fetchCustomerBalances(shopId),
    ]);

    let totalPaid = 0;
    let totalDue = 0;

    transactions.forEach(t => {
      if (t.status === 'completed' || t.status === 'paid') {
        totalPaid += safeNumber(t.amount_paid);
      }
      totalDue += safeNumber(t.amount_due);
    });

    customers.forEach(c => {
      const due = safeNumber(c.balance_due);
      if (due > 0) {
        totalDue += due;
      }
    });

    return {
      total_paid: totalPaid,
      total_due: totalDue,
    };
  } catch (error) {
    console.error('Error fetching customer payment summary:', error);
    return { total_paid: 0, total_due: 0 };
  }
};